import {
  blue,
  Checkmark3Icon,
  FaroButton,
  FaroChip,
  FaroPopover,
  FaroPopoverProps,
  FaroText,
  grey,
  TextField,
} from "@faro-lotv/flat-ui";
import type { ConversationFeedback } from "@faro-lotv/service-wires";
import { Paper } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useMemo, useState, type JSX } from "react";

const NEGATIVE_TAG_OPTIONS = [
  "Incorrect or incomplete",
  "Not what I asked for",
  "Slow or buggy",
  "Safety or legal concern",
] as const;

const POSITIVE_TAG_OPTIONS = [
  "Accurate and complete",
  "Exactly what I needed",
  "Easy to understand",
  "Saved me time",
] as const;

/** A selectable feedback tag, derived from the positive and negative tag option lists */
type TagOption =
  | (typeof NEGATIVE_TAG_OPTIONS)[number]
  | (typeof POSITIVE_TAG_OPTIONS)[number];

type FeedbackPopoverProps = Pick<FaroPopoverProps, "onClose"> & {
  /** Whether the popover is open */
  open: boolean;
  /** The element to anchor the popover to */
  anchorEl: HTMLElement | null;
  /** The sentiment of the feedback, used to determine which tag options to show */
  sentiment?: ConversationFeedback["sentiment"];
  /** Called when the user submits feedback with an optional comment and tags */
  onSubmitFeedback(
    feedback: ConversationFeedback["comment"],
    tags: ConversationFeedback["tags"],
  ): Promise<void>;
};

/**
 * @returns a popover component for collecting user feedback on AI chat interactions.
 * Displays selectable tag chips based on the feedback sentiment (positive/negative)
 * and an optional free-text comment field. Shows a confirmation message after submission.
 */
export function FeedbackPopover({
  anchorEl,
  open,
  sentiment,
  onSubmitFeedback,
  onClose,
}: FeedbackPopoverProps): JSX.Element {
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
  const [comment, setComment] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const tagOptions =
    sentiment === "Positive" ? POSITIVE_TAG_OPTIONS : NEGATIVE_TAG_OPTIONS;

  const toggleOption = useCallback((tag: TagOption) => {
    setSelectedTags((prevTags) => {
      if (prevTags.includes(tag)) {
        return prevTags.filter((o) => o !== tag);
      }
      return [...prevTags, tag];
    });
  }, []);

  const onSubmit = useCallback(() => {
    setIsSubmitted(true);
    onSubmitFeedback(
      comment || undefined,
      selectedTags.length ? selectedTags : undefined,
    );
  }, [selectedTags, comment, onSubmitFeedback]);

  const body = useMemo(() => {
    if (isSubmitted) {
      return (
        <Stack sx={{ gap: 2, padding: 2, alignItems: "center" }}>
          <FaroText variant="heading14">Thanks for your feedback!</FaroText>
          <FaroText variant="bodyM">
            It helps us improve the experience and make the AI more useful for
            you.
          </FaroText>
          <Paper
            sx={{
              mt: 2,
              width: "84px",
              height: "84px",
              textAlign: "center",
              alignContent: "center",
            }}
          >
            <Checkmark3Icon
              sx={{
                width: "60px",
                height: "60px",
                color: blue[500],
              }}
            />
          </Paper>
        </Stack>
      );
    }
    return (
      <Stack sx={{ gap: 2 }}>
        <FaroText variant="bodyM" color={grey[600]}>
          Tell us about your experience using our AI chat
        </FaroText>

        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
          {tagOptions.map((option) => (
            <FaroChip
              key={option}
              label={<FaroText variant="bodyS">{option}</FaroText>}
              textTransform="initial"
              variant="outlined"
              onClick={() => toggleOption(option)}
              icon={
                selectedTags.includes(option) ? <Checkmark3Icon /> : undefined
              }
              backgroundColor={grey[0]}
            />
          ))}
        </Stack>

        <TextField
          text={comment}
          onTextChanged={setComment}
          placeholder="Share details"
          multiline
          minRows={4}
          fullWidth
        />

        <FaroButton variant="primary" onClick={onSubmit}>
          Submit feedback
        </FaroButton>
      </Stack>
    );
  }, [comment, isSubmitted, onSubmit, selectedTags, tagOptions, toggleOption]);

  return (
    <FaroPopover
      placement="auto-start"
      closeOnClickOutside
      onClose={onClose}
      open={open}
      anchorEl={anchorEl}
      showCloseButton={isSubmitted}
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: grey[0],
        },
      }}
      title={
        isSubmitted ? null : (
          <FaroText variant="heading16" sx={{ textTransform: "none" }}>
            Share feedback on FARO AI
          </FaroText>
        )
      }
    >
      {body}
    </FaroPopover>
  );
}
